import type { AnchorHTMLAttributes } from "react";
import { getComponent } from "../../../../client";
import { BlockImage } from "../../../shared/components/BlockImage/BlockImage";
import { BlockRichText } from "../../../shared/components/BlockRichText/BlockRichText";
import type { ITeamMemberCardProps } from "./TeamMemberCard.props";

export const TeamMemberCard = ({
  biography,
  links,
  name,
  portrait,
  role,
}: ITeamMemberCardProps) => {
  const Link = getComponent<AnchorHTMLAttributes<HTMLAnchorElement>>("Link");

  return (
    <article className="flex min-w-0 flex-col items-start overflow-hidden rounded-[14px] border border-foreground/20 p-4 text-start md:p-5">
      <BlockImage
        className="w-full"
        effectiveAspectRatio="3:4"
        image={portrait}
        imageClassName="object-cover"
      />
      <h3 className="mt-6 text-[24px]/[1.15] font-semibold tracking-[-0.02em] [overflow-wrap:anywhere]">
        {name}
      </h3>
      {role ? (
        <p className="mt-2 font-mono text-[12px]/[1.45] text-onSurface-500 [overflow-wrap:anywhere]">
          {role}
        </p>
      ) : null}
      {biography ? (
        <BlockRichText
          className="mt-5 min-w-0 max-w-[62ch] text-base/[1.55] text-onSurface-500 [overflow-wrap:anywhere] [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-4 [&_ul]:ml-5 [&_ul]:list-disc"
          content={biography}
        />
      ) : null}
      {Link && links?.length ? (
        <ul className="mt-6 flex min-w-0 max-w-full flex-wrap gap-x-5 gap-y-2">
          {links.map((link, index) => {
            const opensNewTab = link.openInNewTab === true;

            return (
              <li className="min-w-0 max-w-full" key={`${link.href}-${index}`}>
                <Link
                  aria-label={link.accessibilityLabel ?? link.label}
                  className="inline-flex min-h-6 max-w-full min-w-0 items-center px-1 font-mono text-[12px]/[1.45] underline decoration-foreground/40 underline-offset-4 transition-colors duration-200 [overflow-wrap:anywhere] hover:text-electric-blue focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring"
                  href={link.href}
                  rel={opensNewTab ? "noopener noreferrer" : undefined}
                  target={opensNewTab ? "_blank" : undefined}
                >
                  {link.label}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </article>
  );
};

TeamMemberCard.displayName = "TeamMemberGrid::TeamMemberCard";
